import { HOME_INSURANCE_STEPS } from '@app/app/esales/home/_sections/steps/HOME_INSURANCE_STEPS';
import { CommonType } from '@app/domain/model/CommonType';
import { HomeInsuranceCode } from '@app/domain/model/eshop/home/HomeInsuranceCode';
import { PaymentMethodType } from '@app/domain/model/PaymentMethodType';
import { PaymentType } from '@app/domain/model/PaymentType';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { array, boolean, number, object, string } from 'yup';
import { emailMatcher } from '@app/app/_validation/matchers/emailMatcher';

export const PurchaseHomeInsuranceSteps = {
    [HOME_INSURANCE_STEPS.OBJECT_SUMMARY_READ_ONLY]: object({
        constructionType: string().when('$isConstructionTypeUnknown', {
            is: true,
            then: schema => schema.required('profile.validation.fieldRequired'),
            otherwise: schema => schema.optional(),
        }),
        pledgedToBank: string()
            .required('profile.validation.fieldRequired')
            .oneOf([CommonType.Yes, CommonType.No], 'profile.validation.fieldRequired'),
        leasing: string().when('pledgedToBank', {
            is: CommonType.Yes,
            then: schema => schema.required('profile.validation.fieldRequired'),
            otherwise: schema => schema.optional(),
        }),
        leasingName: string().when('pledgedToBank', {
            is: CommonType.Yes,
            then: schema => schema.required('profile.validation.fieldRequired'),
            otherwise: schema => schema.optional(),
        }),
    }),
    [HOME_INSURANCE_STEPS.OBJECT_SUMMARY_EDITABLE]: object({
        objectType: string().when('$isPropertyDataFound', {
            is: true,
            then: schema => schema.optional(),
            otherwise: schema => schema.required('profile.validation.fieldRequired'),
        }),
        constructionType: string().when('$isPropertyDataFound', {
            is: true,
            then: schema => schema.optional(),
            otherwise: schema => schema.required('profile.validation.fieldRequired'),
        }),
        totalArea: number().when('$isPropertyDataFound', {
            is: true,
            then: schema => schema.optional(),
            otherwise: schema =>
                schema
                    .required('profile.validation.fieldRequired')
                    .min(1, 'profile.validation.fieldRequired')
                    .when('objectType', {
                        is: (val: string) => val === HomeInsuranceCode.Apartment || val === HomeInsuranceCode.Cottage,
                        then: schema => schema.max(150, 'eshop.home.validation.totalAreaMoreThan150'),
                        otherwise: schema =>
                            schema.when('objectType', {
                                is: HomeInsuranceCode.House,
                                then: schema => schema.max(350, 'eshop.home.validation.totalAreaMoreThan350'),
                            }),
                    }),
        }),
        constructionYear: number().when('$isPropertyDataFound', {
            is: true,
            then: schema => schema.optional(),
            otherwise: schema =>
                schema.required('profile.validation.fieldRequired').min(1, 'profile.validation.fieldRequired'),
        }),
        pledgedToBank: string().when('$isPropertyDataFound', {
            is: true,
            then: schema => schema.optional(),
            otherwise: schema =>
                schema
                    .required('profile.validation.fieldRequired')
                    .oneOf([CommonType.Yes, CommonType.No], 'profile.validation.fieldRequired'),
        }),
        leasing: string().when('pledgedToBank', {
            is: CommonType.Yes,
            then: schema => schema.required('profile.validation.fieldRequired'),
            otherwise: schema => schema.optional(),
        }),
        leasingName: string().when('pledgedToBank', {
            is: CommonType.Yes,
            then: schema => schema.required('profile.validation.fieldRequired'),
            otherwise: schema => schema.optional(),
        }),
    }),
    [HOME_INSURANCE_STEPS.CHOOSE_INSURANCE]: object({
        homeInsuranceRisk: string().required('profile.validation.fieldRequired'),
    }),
    [HOME_INSURANCE_STEPS.INSURANCE_BEGIN_DATE]: object({
        beginDate: string()
            .required('profile.validation.fieldRequired')
            .test('min-date', 'eshop.home.validation.beginDateTooEarly', function (value) {
                if (!value) return false;

                const selectedDate = new Date(value + 'T00:00:00');
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const minDate = new Date(today);
                minDate.setDate(minDate.getDate() + 3);
                return selectedDate >= minDate;
            })
            .test('max-date', 'eshop.home.validation.beginDateTooLate', function (value) {
                if (!value) return false;

                const selectedDate = new Date(value + 'T00:00:00');
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const minDate = new Date(today);
                minDate.setDate(minDate.getDate() + 3);
                const maxDate = new Date(minDate);
                maxDate.setDate(maxDate.getDate() + 30);
                return selectedDate <= maxDate;
            }),
    }),
    [HOME_INSURANCE_STEPS.ADDITIONAL_RISKS]: object({
        selectedRiders: array()
            .of(
                object({
                    code: string().nullable(),
                    id: string().required('eshop.home.validation.invalidRiderTypeCode'),
                }),
            )
            .optional()
            .default([]),
    }),
    [HOME_INSURANCE_STEPS.CUSTOMER_LOGIN]: object({}),
    [HOME_INSURANCE_STEPS.POLICYHOLDER_INFORMATION]: object({
        policyholderFirstName: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderLastName: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderStreet: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderHouseNumber: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderCity: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderPostalCode: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderCountry: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderPhoneNumber: string()
            .required('auth.validation.invalidPhoneNumber')
            .test('is-valid-phone', 'auth.validation.invalidPhoneNumber', value => {
                if (!value) {
                    return false;
                }

                return isValidPhoneNumber(value, 'LT');
            }),
        policyholderEmail: string()
            .email('profile.validation.invalidEmail')
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderEmailConfirmation: string()
            .min(1, 'profile.validation.fieldRequired')
            .matches(emailMatcher, 'profile.validation.invalidEmail')
            .required('profile.validation.fieldRequired')
            .test('emails-match', 'eshop.transport.validation.emailsDoNotMatch', function (value) {
                return value === this.parent.policyholderEmail;
            }),
    }),
    [HOME_INSURANCE_STEPS.SUMMARY]: object({
        uniqueNumber: string().when('$isPropertyDataFound', {
            is: true,
            then: schema => schema.optional(),
            otherwise: schema =>
                schema
                    .required('profile.validation.fieldRequired')
                    .matches(/^\d{4}-\d{4}-\d{4}(:\d{4})?$/, 'eshop.home.validation.invalidUniqueNumber'),
        }),
        acceptToPrivacyPolicy: boolean()
            .oneOf([true], 'eshop.home.validation.mustAcceptPrivacyPolicy')
            .required('eshop.home.validation.mustAcceptPrivacyPolicy'),
        acceptToHomeRules: boolean()
            .oneOf([true], 'eshop.home.validation.mustAcceptHomeRules')
            .required('eshop.home.validation.mustAcceptHomeRules'),
        acceptToMarketingCnl: boolean().optional(),
        acceptToMarketingCl: boolean().optional(),
        pledgedToBank: string().when('$isPropertyDataFound', {
            is: true,
            then: schema => schema.optional(),
            otherwise: schema =>
                schema
                    .required('profile.validation.fieldRequired')
                    .oneOf([CommonType.Yes, CommonType.No], 'profile.validation.fieldRequired'),
        }),
        leasing: string().when('pledgedToBank', {
            is: CommonType.Yes,
            then: schema => schema.required('profile.validation.fieldRequired'),
            otherwise: schema => schema.optional(),
        }),
        leasingName: string().when('pledgedToBank', {
            is: CommonType.Yes,
            then: schema => schema.required('profile.validation.fieldRequired'),
            otherwise: schema => schema.optional(),
        }),
    }),
    [HOME_INSURANCE_STEPS.PAYMENT.OPTIONS]: object({
        paymentType: string()
            .required('profile.validation.fieldRequired')
            .oneOf(
                [PaymentType.Subscription, PaymentType.ManualPayment],
                'eshop.general.validation.invalidPaymentType',
            ),
    }),
    [HOME_INSURANCE_STEPS.PAYMENT.METHOD_TYPE]: object({
        paymentMethodType: string()
            .required('profile.validation.fieldRequired')
            .oneOf(
                [PaymentMethodType.Card, PaymentMethodType.Banks],
                'eshop.general.validation.invalidPaymentMethodType',
            ),
        acceptCardAndPaymentProviderRules: boolean().when('paymentMethodType', {
            is: PaymentMethodType.Card,
            then: schema => schema.is([true], 'eshop.general.validation.mustAcceptPaymentProviderRules'),
            otherwise: schema => schema.optional(),
        }),
    }),
    [HOME_INSURANCE_STEPS.PAYMENT.METHOD]: object({
        paymentMethod: string().when('paymentMethodType', {
            is: PaymentMethodType.Banks,
            then: schema => schema.required('profile.validation.fieldRequired'),
            otherwise: schema => schema.optional(),
        }),
    }),
};
